/** Deep links into Torn, plus the bonus-name → bonus-id table they need. */

import type { Offer } from "../state/types.ts";

/**
 * Torn's item-market search accepts bonus filters by numeric id, so a BUY link
 * for a ranked weapon can land on a pre-filtered search rather than the raw
 * item page.
 */
export const BONUS_IDS: Record<string, number> = {
  achilles: 50,
  assassinate: 72,
  backstab: 52,
  berserk: 54,
  bleed: 57,
  blindfire: 33,
  blindside: 51,
  bloodlust: 85,
  burn: 30,
  comeback: 67,
  conserve: 55,
  cripple: 45,
  crusher: 49,
  cupid: 47,
  deadeye: 63,
  deadly: 62,
  demoralize: 36,
  disarm: 86,
  "double tap": 105,
  "double-edged": 74,
  empower: 87,
  eviscerate: 56,
  execute: 75,
  finale: 82,
  focus: 79,
  freeze: 38,
  frenzy: 80,
  fury: 64,
  grace: 53,
  hazardous: 34,
  "home run": 83,
  irradiate: 102,
  lacerate: 89,
  motivation: 61,
  paralyze: 59,
  parry: 84,
  penetrate: 101,
  plunder: 21,
  poison: 32,
  powerful: 68,
  proficience: 14,
  puncture: 66,
  quicken: 88,
  rage: 65,
  revitalize: 41,
  roshambo: 43,
  shock: 120,
  slow: 44,
  smash: 104,
  smurf: 73,
  specialist: 71,
  spray: 35,
  stricken: 20,
  stun: 58,
  suppress: 60,
  "sure shot": 78,
  throttle: 48,
  toxin: 103,
  warlord: 81,
  weaken: 46,
  "wind-up": 76,
  wither: 42,
};

/** Bonus names Torn's `/v2/market/{id}/itemmarket?bonus=` parameter accepts. */
export const WEAPON_BONUS_NAMES = [
  "Achilles", "Assassinate", "Backstab", "Berserk", "Bleed", "Blindfire", "Blindside",
  "Bloodlust", "Burn", "Comeback", "Conserve", "Cripple", "Crusher", "Cupid", "Deadeye",
  "Deadly", "Demoralize", "Disarm", "Double-edged", "Double Tap", "Emasculate", "Empower",
  "Eviscerate", "Execute", "Expose", "Finale", "Focus", "Freeze", "Frenzy", "Fury", "Grace",
  "Hazardous", "Home run", "Irradiate", "Lacerate", "Motivation", "Paralyze", "Parry",
  "Penetrate", "Plunder", "Poison", "Powerful", "Proficience", "Puncture", "Quicken", "Rage",
  "Revitalize", "Roshambo", "Shock", "Sleep", "Slow", "Smash", "Smurf", "Specialist", "Spray",
  "Storage", "Stricken", "Stun", "Suppress", "Sure Shot", "Throttle", "Toxin", "Warlord",
  "Weaken", "Wind-up", "Wither",
] as const;

export function itemImage(itemId: number): string {
  return `https://www.torn.com/images/items/${itemId}/large.png`;
}

export function itemMarketUrl(itemId: number, bonuses: string[] = []): string {
  const base =
    `https://www.torn.com/page.php?sid=ItemMarket#/market/view=search` +
    `&itemID=${itemId}&sortField=price&sortOrder=ASC`;

  const ids: number[] = [];
  const seen = new Set<number>();
  for (const name of bonuses) {
    const id = BONUS_IDS[name.toLowerCase()];
    if (id === undefined || seen.has(id)) continue;
    seen.add(id);
    ids.push(id);
  }

  return ids.reduce((url, id, index) => `${url}&bonuses[${index}]=${id}`, base);
}

export function bazaarUrl(sellerId: number): string {
  return `https://www.torn.com/bazaar.php?userId=${sellerId}`;
}

export function profileUrl(playerId: number): string {
  return `https://www.torn.com/profiles.php?XID=${playerId}`;
}

/** Where clicking an offer should take you. */
export function buyUrl(offer: Offer): string {
  if (offer.source === "bazaar" && offer.sellerId !== null) return bazaarUrl(offer.sellerId);
  const bonuses = offer.details?.bonuses.map((bonus) => bonus.name) ?? [];
  return itemMarketUrl(offer.itemId, bonuses);
}
